import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import AppShell from './components/Layout/AppShell';
import { useAuth } from './hooks/auth';
import { useAnalytics } from './hooks/useAnalytics';

import { ErrorBoundary } from './components/UI/ErrorBoundary';
import { LiveAnnouncerProvider } from './components/UI/LiveAnnouncer';
import { ThemeProvider } from './context/ThemeContext';
import { MovePlantProvider } from './context/MovePlantContext';
import { useUserPreferences } from './hooks/useUserPreferences';
import { changeLanguage } from './i18n';

// Lazy load pages for better performance
const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const Controls = React.lazy(() => import('./pages/Controls/Controls'));
const ServiceConsole = React.lazy(() => import('./pages/Field/ServiceConsole'));
const Dosing = React.lazy(() => import('./pages/Dosing/Dosing'));
const AutomationRules = React.lazy(() => import('./pages/Automation/AutomationRules'));
const ActuatorConfigPage = React.lazy(() => import('./pages/Controls/ActuatorConfigPage'));
const ActuatorHistoryPage = React.lazy(() => import('./pages/Controls/ActuatorHistoryPage'));
const Analytics = React.lazy(() => import('./pages/Analytics/Analytics'));
const Settings = React.lazy(() => import('./pages/Settings/Settings'));
const Schedule = React.lazy(() => import('./pages/Schedule/Schedule'));
const UnifiedCalendar = React.lazy(() => import('./pages/Calendar/UnifiedCalendar'));
const Login = React.lazy(() => import('./pages/Login/Login'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword/ResetPassword'));
const Profile = React.lazy(() => import('./pages/Profile/Profile'));
const Notifications = React.lazy(() => import('./pages/Notifications/NotificationsPage'));
const Batches = React.lazy(() => import('./pages/Growth/Batches'));
const Journal = React.lazy(() => import('./pages/Growth/Journal'));
const Recipes = React.lazy(() => import('./pages/Growth/Recipes'));
const BatchDetail = React.lazy(() =>
  import('./pages/Growth/BatchDetail').then((m) => ({ default: m.BatchDetail })),
);
const DiscordCallback = React.lazy(() => import('./pages/DiscordCallback/DiscordCallback'));
const GoogleCallback = React.lazy(() => import('./pages/GoogleCallback/GoogleCallback'));
const SafetyRules = React.lazy(() => import('./pages/Safety/SafetyRules'));
const SafetyViolations = React.lazy(() => import('./pages/Safety/SafetyViolations'));
const SafetyAnalytics = React.lazy(() => import('./pages/Safety/SafetyAnalytics'));
const AutomationHub = React.lazy(() => import('./pages/Automation/AutomationHub'));

import { AdminLayout } from './components/Layout/AdminLayout';

// Sensor pages
const SensorListPage = React.lazy(() =>
  import('./pages/sensors/SensorListPage').then((m) => ({
    default: m.SensorListPage,
  })),
);
const SensorDetailPage = React.lazy(() =>
  import('./pages/sensors/SensorDetailPage').then((m) => ({
    default: m.SensorDetailPage,
  })),
);

// Gardens Pages
const GardenForm = React.lazy(() => import('./pages/Gardens/GardenForm'));
const MyGarden = React.lazy(() => import('./pages/Gardens/MyGarden'));

// Growing Areas Pages
const GrowingAreaList = React.lazy(() => import('./pages/GrowingAreas/GrowingAreaList'));
const GrowingAreaDetail = React.lazy(() => import('./pages/GrowingAreas/GrowingAreaDetail'));
const GrowingAreaForm = React.lazy(() => import('./pages/GrowingAreas/GrowingAreaForm'));

// Reservoirs Page
const ReservoirList = React.lazy(() => import('./pages/Reservoirs/ReservoirList'));

// Crop Plans Pages
const CropPlanList = React.lazy(() => import('./pages/CropPlans/CropPlanList'));
const CropPlanTemplateList = React.lazy(() => import('./pages/CropPlans/CropPlanTemplateList'));
const CropPlanDetail = React.lazy(() => import('./pages/CropPlans/CropPlanDetail'));
const CropPlanForm = React.lazy(() => import('./pages/CropPlans/CropPlanForm'));

// Tasks Page
const TaskList = React.lazy(() => import('./pages/Tasks/TaskList'));

// Plants Page
const PlantDetail = React.lazy(() => import('./pages/Plants/PlantDetail'));

// Plant-O-Pedia Pages
const SpeciesList = React.lazy(() => import('./pages/PlantOPedia/SpeciesList'));
const SpeciesDetail = React.lazy(() => import('./pages/PlantOPedia/SpeciesDetail'));
const SpeciesForm = React.lazy(() => import('./pages/PlantOPedia/SpeciesForm'));
const VarietyDetail = React.lazy(() => import('./pages/PlantOPedia/VarietyDetail'));
const PestsList = React.lazy(() => import('./pages/PlantOPedia/PestsList'));
const PestDetail = React.lazy(() => import('./pages/PlantOPedia/PestDetail'));
const DiseasesList = React.lazy(() => import('./pages/PlantOPedia/DiseasesList'));
const DiseaseDetail = React.lazy(() => import('./pages/PlantOPedia/DiseaseDetail'));
const SpeciesCompare = React.lazy(() => import('./pages/PlantOPedia/SpeciesCompare'));

// Help pages
const HelpGlossary = React.lazy(() => import('./pages/Help/HelpGlossary'));

// Admin pages
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const UserManagement = React.lazy(() => import('./pages/admin/UserManagement'));
const DeviceManagement = React.lazy(() => import('./pages/admin/DeviceManagement'));
const HardwareMetrics = React.lazy(() => import('./pages/admin/HardwareMetrics'));
const AuditLogs = React.lazy(() => import('./pages/admin/AuditLogs'));
const FirmwareManagement = React.lazy(() => import('./pages/admin/FirmwareManagement'));
const ApiDashboard = React.lazy(() => import('./pages/admin/ApiDashboard'));
const KnowledgeSourcesPage = React.lazy(() => import('./pages/admin/KnowledgeSourcesPage'));

// Public lite preview (no auth required)
const LitePreview = React.lazy(() => import('./pages/LitePreview/LitePreview'));

// Interlock pages
const InterlockMonitorPage = React.lazy(() => import('./pages/Interlocks/InterlockMonitorPage'));
const InterlockLogPage = React.lazy(() => import('./pages/Interlocks/InterlockLogPage'));
const InterlockOverridesPage = React.lazy(() => import('./pages/Interlocks/InterlockOverridesPage'));

import { Skeleton } from './components/UI/Skeleton';

// Loading component - pouziva Skeleton pro konzistentni vzhled
const PageLoader: React.FC = () => (
  <div className="min-h-[60vh] p-6">
    <Skeleton.Dashboard cards={4} columns={4} className="mb-6" />
    <Skeleton.Chart height={200} />
  </div>
);

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Admin route component - requires admin, god or technician role
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Povolene role pro admin sekci
  const allowedRoles = ['admin', 'god', 'technician'];
  if (!user?.role || !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

/** Syncs user's language preference with i18n on login / preference change */
const LanguageSync: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { preferences } = useUserPreferences({ enabled: isAuthenticated });

  useEffect(() => {
    if (preferences?.language) {
      changeLanguage(preferences.language);
    }
  }, [preferences?.language]);

  return null;
};

const PublicOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const AnalyticsTracker: React.FC = () => {
  const { trackPageView } = useAnalytics();
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname, trackPageView]);

  return null;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <MovePlantProvider>
          <LiveAnnouncerProvider debounceTime={1000} maxQueue={5}>
            <div className="flex flex-col min-h-screen text-gray-900 dark:text-zinc-100 bg-gray-50 dark:bg-black transition-colors duration-300">
              <LanguageSync />
              <AnalyticsTracker />
              <Routes>
                {/* Public routes */}
                <Route
                  path="/login"
                  element={
                    <PublicOnlyRoute>
                      <Suspense fallback={<PageLoader />}>
                        <Login />
                      </Suspense>
                    </PublicOnlyRoute>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <PublicOnlyRoute>
                      <Suspense fallback={<PageLoader />}>
                        <ForgotPassword />
                      </Suspense>
                    </PublicOnlyRoute>
                  }
                />

                <Route
                  path="/reset-password"
                  element={
                    <PublicOnlyRoute>
                      <Suspense fallback={<PageLoader />}>
                        <ResetPassword />
                      </Suspense>
                    </PublicOnlyRoute>
                  }
                />

                {/* Google OAuth callback (public - handles login/register) */}
                <Route
                  path="/auth/google/callback"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <GoogleCallback />
                    </Suspense>
                  }
                />

                {/* Public lite preview — no auth required */}
                <Route
                  path="/lite-preview"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <LitePreview />
                    </Suspense>
                  }
                />

                {/* Protected routes */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <AppShell>
                        <Suspense fallback={<PageLoader />}>
                          <Routes>
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/controls" element={<Controls />} />
                            <Route path="/servis" element={<ServiceConsole />} />
                            <Route path="/dosing" element={<Dosing />} />
                            <Route path="/automation-hub" element={<AutomationHub />} />
                            <Route path="/automation" element={<AutomationRules />} />
                            <Route path="/safety" element={<SafetyRules />} />
                            <Route path="/safety/violations" element={<SafetyViolations />} />
                            <Route path="/safety/analytics" element={<SafetyAnalytics />} />
                            <Route
                              path="/controls/:actuatorId/config"
                              element={<ActuatorConfigPage />}
                            />
                            <Route
                              path="/controls/:actuatorId/history"
                              element={<ActuatorHistoryPage />}
                            />
                            <Route path="/analytics" element={<Analytics />} />
                            {/* Hardware Page removed - moved to Dashboard Widget */}
                            <Route path="/sensors" element={<SensorListPage />} />
                            <Route path="/sensors/:sensorId" element={<SensorDetailPage />} />
                            <Route path="/schedule" element={<Schedule />} />
                            <Route path="/calendar" element={<UnifiedCalendar />} />
                            <Route path="/settings" element={<Settings />} />
                            {/* Discord OAuth2 callback */}
                            <Route path="/auth/discord/callback" element={<DiscordCallback />} />
                            <Route path="/notifications" element={<Notifications />} />
                            <Route path="/profile" element={<Profile />} />
                            {/* Gardens Routes — integrated into GrowingAreas */}
                            <Route
                              path="/gardens"
                              element={<Navigate to="/growing-areas" replace />}
                            />
                            <Route path="/gardens/new" element={<GardenForm />} />
                            <Route
                              path="/gardens/:gardenId"
                              element={<Navigate to="/growing-areas" replace />}
                            />
                            <Route path="/gardens/:gardenId/edit" element={<GardenForm />} />
                            {/* Growth Routes */}
                            <Route
                              path="/branches"
                              element={<Navigate to="/growing-areas" replace />}
                            />
                            <Route
                              path="/branches/:branchId"
                              element={<Navigate to="/growing-areas" replace />}
                            />
                            <Route path="/my-garden" element={<MyGarden />} />
                            <Route path="/growing-areas" element={<GrowingAreaList />} />
                            <Route path="/growing-areas/new" element={<GrowingAreaForm />} />
                            <Route path="/growing-areas/:areaId" element={<GrowingAreaDetail />} />
                            <Route
                              path="/growing-areas/:areaId/edit"
                              element={<GrowingAreaForm />}
                            />
                            <Route path="/reservoirs" element={<ReservoirList />} />
                            <Route path="/crop-plans" element={<CropPlanList />} />
                            <Route path="/crop-plans/templates" element={<CropPlanTemplateList />} />
                            <Route path="/crop-plans/new" element={<CropPlanForm />} />
                            <Route path="/crop-plans/:planId" element={<CropPlanDetail />} />
                            <Route path="/crop-plans/:planId/edit" element={<CropPlanForm />} />
                            <Route path="/tasks" element={<TaskList />} />
                            <Route path="/growth/batches" element={<Batches />} />
                            <Route path="/growth/batches/:batchId" element={<BatchDetail />} />
                            <Route path="/growth/journal" element={<Journal />} />
                            <Route path="/growth/recipes" element={<Recipes />} />
                            <Route
                              path="/growth/dosing-tables"
                              element={<Navigate to="/growth/recipes?tab=templates" replace />}
                            />
                            {/* Plant Route */}
                            <Route path="/plants/:id" element={<PlantDetail />} />
                            {/* Plant-O-Pedia Routes */}
                            <Route path="/plant-o-pedia" element={<SpeciesList />} />
                            <Route path="/plant-o-pedia/species/new" element={<SpeciesForm />} />
                            <Route path="/plant-o-pedia/species/:id" element={<SpeciesDetail />} />
                            <Route
                              path="/plant-o-pedia/species/:id/edit"
                              element={<SpeciesForm />}
                            />
                            <Route
                              path="/plant-o-pedia/varieties/:id"
                              element={<VarietyDetail />}
                            />
                            <Route path="/plant-o-pedia/pests" element={<PestsList />} />
                            <Route path="/plant-o-pedia/pests/:id" element={<PestDetail />} />
                            <Route path="/plant-o-pedia/diseases" element={<DiseasesList />} />
                            <Route path="/plant-o-pedia/diseases/:id" element={<DiseaseDetail />} />
                            <Route path="/plant-o-pedia/compare" element={<SpeciesCompare />} />
                            <Route
                              path="/strains"
                              element={<Navigate to="/plant-o-pedia" replace />}
                            />
                            {/* Interlock monitoring */}
                            <Route path="/interlocks" element={<InterlockMonitorPage />} />
                            <Route path="/interlocks/log" element={<InterlockLogPage />} />
                            <Route path="/interlocks/overrides" element={<InterlockOverridesPage />} />
                            {/* Help / Glossary */}
                            <Route path="/help" element={<HelpGlossary />} />
                            {/* Admin Routes */}
                            {/* Admin routes moved to separate layout block */}
                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                          </Routes>
                        </Suspense>
                      </AppShell>
                    </ProtectedRoute>
                  }
                />

                {/* Admin Console Routes - Separate Layout */}
                <Route
                  path="/admin/*"
                  element={
                    <AdminRoute>
                      <AdminLayout>
                        <Suspense fallback={<PageLoader />}>
                          <Routes>
                            <Route path="/" element={<AdminDashboard />} />
                            <Route path="/users" element={<UserManagement />} />
                            <Route path="/devices" element={<DeviceManagement />} />
                            <Route path="/hardware-metrics" element={<HardwareMetrics />} />
                            <Route path="/audit-logs" element={<AuditLogs />} />
                            <Route path="/firmware" element={<FirmwareManagement />} />
                            <Route path="/api-dashboard" element={<ApiDashboard />} />
                            <Route path="/knowledge-sources" element={<KnowledgeSourcesPage />} />
                            <Route path="*" element={<Navigate to="/admin" replace />} />
                          </Routes>
                        </Suspense>
                      </AdminLayout>
                    </AdminRoute>
                  }
                />
              </Routes>
            </div>
          </LiveAnnouncerProvider>
        </MovePlantProvider>
      </ThemeProvider>
      {/* Global toast notifications - moved outside to ensure z-index visibility */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'rgba(15, 23, 42, 0.94)',
            color: '#f8fafc',
            border: '1px solid rgba(45, 212, 191, 0.22)',
            boxShadow: '0 18px 60px rgba(15, 23, 42, 0.22)',
            backdropFilter: 'blur(16px)',
            zIndex: 9999,
          },
          success: {
            style: {
              background: 'linear-gradient(135deg, rgba(6, 95, 70, 0.96), rgba(15, 118, 110, 0.96))',
              border: '1px solid rgba(110, 231, 183, 0.24)',
            },
          },
          error: {
            style: {
              background: 'linear-gradient(135deg, rgba(127, 29, 29, 0.96), rgba(153, 27, 27, 0.96))',
              border: '1px solid rgba(252, 165, 165, 0.24)',
            },
          },
        }}
      />
    </ErrorBoundary>
  );
};

export default App;
